import React, { useEffect, useMemo, useRef, useState } from 'react';
import KpiCard from './KpiCard';
import Chip from './Chip';
import { dateLabel, dayLabel, isWeekendOrHoliday, money } from './money';

const columns = [
  ['sales_tax', 'Sales tax'],
  ['ap_operating', 'AP operating'],
  ['ap_inventory', 'AP inventory'],
  ['credit_card', 'Credit card'],
  ['payroll', 'Payroll'],
  ['owner_distribution', 'Owner distributions'],
  ['debt', 'Debt'],
  ['other_outflow', 'Other'],
];

const addDateKey = (value, days) => {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const formatDateKey = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};

const amountSourceLabel = (card) => {
  switch (card.amountSource) {
    case 'statement':
      return 'Statement balance';
    case 'last_payment':
      return 'Last payment';
    case 'bank_payment':
      return `Estimate · previous payment ${formatDateKey(card.estimatedFrom) || '—'}`;
    case 'manual':
      return 'Manual override';
    default:
      return 'No amount';
  }
};

export default function DisbursementCalendarTab({
  data,
  holidays = [],
  loading,
  error,
  week,
  onWeekChange,
  settings,
  history,
  historyLoading,
  historyError,
  onHistoryRequested,
}) {
  const [expanded, setExpanded] = useState(null);
  const [calendarWeek, setCalendarWeek] = useState(week);
  const groupRefs = useRef({});
  const rows = useMemo(
    () => (Array.isArray(data) ? data : data?.data || []),
    [data],
  );
  const hiddenCategories = new Set(
    settings?.settings?.display?.hiddenCategories ||
      settings?.display?.hiddenCategories ||
      [],
  );
  const visibleColumns = columns.filter(([key]) => !hiddenCategories.has(key));
  const groups = useMemo(() => {
    const result = new Map();
    rows.forEach((row) => {
      const index = Number(row.week || 0);
      if (!result.has(index)) result.set(index, []);
      result.get(index).push(row);
    });
    return [...result.entries()].sort(([a], [b]) => a - b);
  }, [rows]);
  const selectedGroup = groups.findIndex(([index]) => index === calendarWeek);
  const currentWeekStart = rows.find((row) => Number(row.week) === 0)?.weekStart
    || rows[0]?.weekStart;
  const largest = rows.reduce(
    (max, row) => (Number(row.total) > Number(max.total || 0) ? row : max),
    {},
  );
  useEffect(() => {
    if (calendarWeek >= 0 && week >= 0 && week !== calendarWeek) {
      setCalendarWeek(week);
    }
  }, [week, calendarWeek]);
  const selectWeek = (nextWeek) => {
    const safe = Math.max(-8, Math.min(12, nextWeek));
    setCalendarWeek(safe);
    if (safe < 0) {
      onHistoryRequested?.();
      return;
    }
    onWeekChange?.(safe);
    groupRefs.current[safe]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        Loading disbursements…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-600">
        No disbursement schedule is available.
      </div>
    );
  }

  if (calendarWeek < 0 && currentWeekStart) {
    const weekStart = addDateKey(currentWeekStart, calendarWeek * 7);
    const endDate = addDateKey(weekStart, 6);
    const historyRows = (history?.data || []).filter(
      (row) => row.date >= weekStart && row.date <= endDate,
    );
    const comparison = history?.priorWeeks?.find((item) => item.weekStart === weekStart);
    const actualWeek = comparison?.actual;
    const forecastWeek = comparison?.week;
    const hiddenCategories = new Set(
      settings?.settings?.display?.hiddenCategories
        || settings?.display?.hiddenCategories
        || [],
    );
    const visibleColumns = columns.filter(([key]) => !hiddenCategories.has(key));
    const value = (weekRow, category) => weekRow?.values?.[category];
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-gray-900">Disbursement calendar</p>
            <p className="text-xs text-gray-500">Week of {formatDateKey(weekStart)} · actual cash activity</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => selectWeek(calendarWeek - 1)}
              disabled={calendarWeek <= -8}
              className="h-8 rounded-md border border-gray-200 px-2 text-gray-700 disabled:opacity-40"
              aria-label="Previous week"
            >
              ←
            </button>
            <span className="min-w-[140px] text-center text-sm font-semibold text-gray-800">
              Week of {formatDateKey(weekStart)}
            </span>
            <button
              type="button"
              onClick={() => selectWeek(calendarWeek + 1)}
              className="h-8 rounded-md border border-gray-200 px-2 text-gray-700"
              aria-label="Next week"
            >
              →
            </button>
          </div>
        </div>
        {historyError && <p className="text-sm text-red-700">{historyError}</p>}
        {historyLoading && <p className="text-sm text-gray-500">Loading prior-week disbursements…</p>}
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-[650px] w-full text-sm">
            <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-right">Forecast</th>
                <th className="px-3 py-2 text-right">Actual</th>
                <th className="px-3 py-2 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {visibleColumns.map(([category, label]) => {
                const forecast = value(forecastWeek, category);
                const actual = value(actualWeek, category);
                return (
                  <tr key={category} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-700">{label}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{forecast == null ? '—' : money(forecast)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{actual == null ? '—' : money(actual)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{actual == null || forecast == null ? '—' : money(actual - forecast)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-[650px] w-full text-sm">
            <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Actual payments</th></tr>
            </thead>
            <tbody>
              {Array.from({ length: 7 }, (_, index) => {
                const date = addDateKey(weekStart, index);
                const row = historyRows.find((item) => item.date === date);
                const lines = visibleColumns.flatMap(([category, label]) => (
                  (row?.lines?.[category] || []).map((line) => ({ category: label, ...line }))
                ));
                return (
                  <tr key={date} className="border-t border-gray-100 align-top">
                    <td className="whitespace-nowrap px-3 py-2 font-medium">{dateLabel(date)}</td>
                    <td className="px-3 py-2">
                      {lines.length ? (
                        <div className="space-y-1">
                          {lines.map((line, lineIndex) => (
                            <div key={`${line.category}-${line.label}-${lineIndex}`} className="flex justify-between gap-4 text-xs">
                              <span><strong className="mr-1 text-gray-500">{line.category}</strong>{line.label}</span>
                              <span className="shrink-0 tabular-nums">{money(line.amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : <span className="text-xs text-gray-400">No categorized payments.</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-gray-900">
            Disbursement calendar
          </p>
          <p className="text-xs text-gray-500">
            Unified scheduled cash out by day and source.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => selectWeek(calendarWeek - 1)}
            disabled={calendarWeek <= -8}
            className="h-8 rounded-md border border-gray-200 px-2 text-gray-700 disabled:opacity-40"
            aria-label="Previous week"
          >
            ←
          </button>
          <span className="min-w-[140px] text-center text-sm font-semibold text-gray-800">
            {`Week ${calendarWeek + 1} — ${dateLabel(groups[selectedGroup]?.[1]?.[0]?.date)}`}
          </span>
          <button
            type="button"
            onClick={() => selectWeek(calendarWeek + 1)}
            disabled={calendarWeek >= 12}
            className="h-8 rounded-md border border-gray-200 px-2 text-gray-700 disabled:opacity-40"
            aria-label="Next week"
          >
            →
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 rounded-lg border border-gray-200 bg-white sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Scheduled cash out"
          value={rows.reduce((sum, row) => sum + Number(row.total || 0), 0)}
          moneyValue
          tone="text-red-700"
        />
        <KpiCard
          label="Scheduled days"
          value={rows.filter((row) => Number(row.total) > 0).length}
        />
        <KpiCard
          label="Largest cash-out day"
          value={largest.date ? dateLabel(largest.date) : '—'}
          caption={largest.date ? money(largest.total) : null}
        />
        <KpiCard
          label="AP operating"
          value={rows.reduce(
            (sum, row) => sum + Number(row.ap_operating || 0),
            0,
          )}
          moneyValue
        />
        <KpiCard
          label="Credit cards"
          value={rows.reduce(
            (sum, row) => sum + Number(row.credit_card || 0),
            0,
          )}
          moneyValue
        />
        <KpiCard
          label="Payroll"
          value={rows.reduce((sum, row) => sum + Number(row.payroll || 0), 0)}
          moneyValue
        />
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-[1200px] w-full text-sm">
          <thead className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            <tr>
              <th className="px-3 py-2">Date</th>
              {visibleColumns.map(([, label]) => (
                <th key={label} className="px-3 py-2 text-right">
                  {label}
                </th>
              ))}
              <th className="px-3 py-2 text-right">Total cash out</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(([groupWeek, groupRows]) => (
              <React.Fragment key={groupWeek}>
                <tr
                  ref={(node) => {
                    groupRefs.current[groupWeek] = node;
                  }}
                  className="bg-gray-50"
                >
                  <td
                    colSpan={visibleColumns.length + 2}
                    className="px-3 py-2 font-semibold text-gray-800"
                  >
                    {`Week ${groupWeek + 1} — ${dateLabel(
                      groupRows[0]?.date,
                    )} (Mon) – ${dateLabel(
                      groupRows[groupRows.length - 1]?.date,
                    )} (Sun)`}
                  </td>
                </tr>
                {groupRows.map((row) => (
                  <React.Fragment key={row.date}>
                    <tr className={`border-t border-gray-100 hover:border-gray-200 ${isWeekendOrHoliday(row.date, holidays.length ? holidays : data.holidays) ? 'bg-rose-50' : ''}`}>
                      <td className="px-3 py-2 font-medium">
                        {dayLabel(row.date)}
                      </td>
                      {visibleColumns.map(([key]) => (
                        <td
                          key={key}
                          className="px-3 py-2 text-right tabular-nums text-red-700"
                        >
                          {money(row[key])}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-red-700">
                        <button
                          type="button"
                          onClick={() =>
                            row.cards?.length &&
                            setExpanded(expanded === row.date ? null : row.date)
                          }
                        >
                          {money(row.total)}
                        </button>
                      </td>
                    </tr>
                    {expanded === row.date &&
                      row.cards?.map((card) => (
                        <tr
                          key={`${row.date}-${card.cardId}`}
                          className="bg-gray-50 text-xs"
                        >
                          <td className="px-6 py-2">
                            {dateLabel(row.date)} · {card.cardName}{' '}
                            {card.mask ? `···${card.mask}` : ''}
                          </td>
                          <td
                            colSpan={visibleColumns.length}
                            className="px-3 py-2"
                          >
                            {amountSourceLabel(card)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {money(card.amount)}{' '}
                            <Chip tone={card.state}>{card.state}</Chip>
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="px-4 py-8 text-center text-sm text-gray-600">
            No scheduled outflows for this horizon.
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Future disbursement edits are made from the daily forecast so the cash
        chain remains traceable.
      </p>
    </div>
  );
}
